import { useShowPolicyBanner } from "@capone/common";
import { getRoomPolicyComplianceType } from "@capone/common/src/utils/hotelShopPolicyCompliance";
import { Box, IconButton, Tooltip, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  getIsFreeBreakfast,
  textConstants as halifaxTextConstants,
  HotelInformationalBanner,
  HotelShopChooseRoom,
  Icon,
  IconName,
  VoidWindowNotice, isSpendAndGetOffer,
} from "halifax";
import React, { useContext, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  CancellationReason,
  CorpRoomInfoProducts,
  CustomerAccountRole,
  RestrictedToProductsEnum,
  SelectedTravelOfferScreen,
} from "redmond";
import { config } from "../../../../api/config";
import { ClientContext } from "../../../../App";
import {
  AVAILABLE,
  getExperimentVariant,
  HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { CalendarPickerButton } from "../../../search/components/PremierCollectionSearchControl/components/CalendarPickerButton";
import { TravelWalletDetailsBanner } from "../../../travel-wallet/components";
import { DesktopPremierCollectionShopChooseRoomConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";

export interface DesktopPremierCollectionShopChooseRoomProps
  extends DesktopPremierCollectionShopChooseRoomConnectorProps,
    RouteComponentProps {
  variant?: "default" | "lifestyle-collection";
}

export const DesktopPremierCollectionShopChooseRoom = (
  props: DesktopPremierCollectionShopChooseRoomProps
) => {
  const {
    variant = "default",
    largestEarnAccount,
    selectedLodging,
    shopCancelationSummary,
  } = props;
  const expState = useExperiments();
  const { policies } = useContext(ClientContext);
    const showPolicyBanner = useShowPolicyBanner(policies);

  const californiaBill644Variant = getExperimentVariant(
    expState.experiments,
    HOTELS_CALIFORNIA_BILL_644_EXPERIMENT
  );
  const isCaliforniaBill644Experiment = useMemo(
    () => californiaBill644Variant === AVAILABLE,
    [californiaBill644Variant]
  );

  const isXmodRedesign = useMemo(
    () =>
      getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
      AVAILABLE,
    [expState.experiments]
  );

  const showFree24HourCancel =
    isCaliforniaBill644Experiment &&
    shopCancelationSummary?.reasons.includes(
      CancellationReason.CaliforniaBill644
    );

  return (
    <Box
      className={clsx("pc-shop-choose-room-button-and-benefits-wrapper", {
        "lifestyle-collection": variant === "lifestyle-collection",
        "free-breakfast":
          variant === "lifestyle-collection" &&
          selectedLodging?.lodging &&
          getIsFreeBreakfast(selectedLodging?.price?.mealPlan?.kind) &&
          !selectedLodging?.nthNightPromotion,
        "nth-night": selectedLodging?.nthNightPromotion,
      })}
    >
      {variant === "lifestyle-collection" ? (
        <Icon name={IconName.LifestyleCollectionRibbon} />
      ) : (
        <Icon name={IconName.PremierCollectionRibbon} />
      )}
      {variant === "lifestyle-collection" &&
      selectedLodging?.lodging &&
      getIsFreeBreakfast(selectedLodging?.price?.mealPlan?.kind) &&
      !selectedLodging.nthNightPromotion ? (
        <HotelInformationalBanner
          content={
            <Tooltip
              title={
                <Typography className="tooltip-text">
                  {halifaxTextConstants.FREE_BREAKFAST_TOOLTIP}
                </Typography>
              }
              classes={{
                popper: "free-breakfast-tooltip-popper",
                tooltip: "free-breakfast-tooltip-text",
              }}
            >
              <Box className="free-breakfast-banner-content">
                <Typography className="free-breakfast-content-text">
                  Free breakfast for cardholders
                </Typography>
                <Icon name={IconName.InfoCircle} />
              </Box>
            </Tooltip>
          }
          isMobile={false}
          isMap={false}
          iconName={IconName.FreeBreakfast}
          isXmodRedesign={isXmodRedesign}
        />
      ) : null}
      <HotelShopChooseRoom
        {...props}
        datepicker={
          <CalendarPickerButton
            classes={["room-calendar-button"]}
            hideSeparator
          />
        }
        onClickContinue={() =>
          document
            .getElementsByClassName("reserve-cta")[0]
            ?.scrollIntoView({ behavior: "smooth", block: "end" })
        }
        buttonClassName="b2b"
        tenant={config.TENANT}
        policyComplianceType={getRoomPolicyComplianceType(
          props.roomInfoProducts as CorpRoomInfoProducts[]
        )}
        showPolicyBanner={showPolicyBanner}
      />
      {largestEarnAccount && largestEarnAccount.earn.hotelsMultiplier && (
        <Box
          className={clsx("pc-shop-benefits", {
            "nth-night": selectedLodging?.nthNightPromotion,
          })}
        >
          <Box className="pc-benefits-title">
            <Icon name={IconName.StarOutline} />
            <Typography className="pc-benefits-title-text">
              {variant === "lifestyle-collection"
                ? textConstants.LC_BENEFITS_TITLE
                : textConstants.BENEFITS_TITLE}
            </Typography>
          </Box>

          <Typography className="pc-benefits-list">
            {textConstants.getBenefitsList(
              largestEarnAccount.earn.hotelsMultiplier,
              variant,
              largestEarnAccount.customerAccountRole ===
                CustomerAccountRole.Primary,
              largestEarnAccount.rewardsBalance?.currencyDescription ||
                largestEarnAccount.rewardsBalance?.currency
            )}
          </Typography>
          <Box className="additional-benefits">
            <Icon name={IconName.GiftOutline} />
            <Typography className="additional-benefits-text">
              {variant === "lifestyle-collection"
                ? textConstants.LC_ADDITIONAL_BENEFITS
                : textConstants.ADDITIONAL_BENEFITS}
            </Typography>
          </Box>
        </Box>
      )}
      {showFree24HourCancel && <VoidWindowNotice />}
      {selectedLodging?.lodging && selectedLodging.nthNightPromotion ? (
        <HotelInformationalBanner
          content={
            <Tooltip
              title={
                <Typography className="tooltip-text">
                  {selectedLodging.nthNightPromotion?.tooltipDescription}
                </Typography>
              }
              classes={{
                popper: "nth-night-tooltip-popper",
                tooltip: "nth-night-tooltip-text",
              }}
              disableFocusListener={false}
              aria-labelledby={
                selectedLodging.nthNightPromotion?.tooltipDescription
              }
            >
              <Box className="nth-night-banner-content">
                <Typography className="nth-night-content-text">
                  {selectedLodging.nthNightPromotion?.description}
                </Typography>
                <IconButton aria-labelledby="nth-night-info-label">
                  <Icon name={IconName.InfoCircle} />
                </IconButton>
              </Box>
            </Tooltip>
          }
          isMobile={false}
          isMap={false}
          iconName={IconName.Sparkle}
          isXmodRedesign={isXmodRedesign}
        />
      ) : null}

      {!isSpendAndGetOffer(
        selectedLodging?.bestOfferThisLodging?.descriptions
      ) && (
        <TravelWalletDetailsBanner
          offer={selectedLodging?.bestOfferThisLodging!}
          showButton={false}
          className="hotel-shop-offer"
          screen={SelectedTravelOfferScreen.PREMIUM_STAYS_DETAILS}
          restrictedToProduct={RestrictedToProductsEnum.Lodging}
          {...props}
        />
      )}
    </Box>
  );
};
